// Window-safe fetch for Electron's sandboxed renderer.
//
// Chromium throws:
//   Failed to execute 'fetch' on 'Window': Illegal invocation
// when the native Window.fetch method is extracted and later called without
// the Window as `this`. Bundlers, default-arg capture (`fetchImpl = fetch`),
// and some Electron/contextIsolation edges all recreate that failure mode.
//
// Always invoke through Reflect.apply / .call with an explicit receiver, and
// fall back to XHR if even that Illegal-invokes (defensive for broken polyfills).

function isIllegalInvocation(err) {
  return /Illegal invocation/i.test(String(err?.message || err || ''));
}

function xhrFetch(input, init = {}) {
  const method = String(init.method || 'GET').toUpperCase();
  const url = typeof input === 'string' ? input : String(input?.url || input);
  const headers = init.headers || {};
  const body = init.body;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.responseType = 'arraybuffer';

    if (headers && typeof headers === 'object') {
      for (const [key, value] of Object.entries(headers)) {
        if (value != null) xhr.setRequestHeader(key, String(value));
      }
    }

    const signal = init.signal;
    const onAbort = () => {
      try {
        xhr.abort();
      } catch {
        /* ignore */
      }
      reject(signal?.reason || new DOMException('Aborted', 'AbortError'));
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }

    xhr.onload = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
      const buf = xhr.response || new ArrayBuffer(0);
      const responseHeaders = new Headers();
      const raw = xhr.getAllResponseHeaders() || '';
      raw
        .trim()
        .split(/[\r\n]+/)
        .forEach((line) => {
          const idx = line.indexOf(':');
          if (idx > 0) {
            responseHeaders.append(line.slice(0, idx).trim(), line.slice(idx + 1).trim());
          }
        });
      resolve(
        new Response(buf, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders,
        }),
      );
    };
    xhr.onerror = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
      reject(new TypeError('Failed to fetch'));
    };
    xhr.send(body ?? null);
  });
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export function safeFetch(input, init) {
  const receiver =
    typeof window !== 'undefined' ? window : typeof globalThis !== 'undefined' ? globalThis : null;
  const fn = receiver?.fetch;

  if (typeof fn === 'function' && receiver) {
    try {
      return Promise.resolve(Reflect.apply(fn, receiver, [input, init]));
    } catch (err) {
      if (!isIllegalInvocation(err)) return Promise.reject(err);
      // fall through to XHR
    }
  }

  if (typeof XMLHttpRequest === 'function') {
    return xhrFetch(input, init);
  }

  return Promise.reject(new TypeError('safeFetch: no fetch implementation available'));
}
